/**
 * Butikk — the mosque's products, its orders and how they are delivered. Iteration 3: pictures with a description,
 * availability, and a list that can be scanned; store checkout (owner, 2026-10-18): orders paid with Vipps, stock.
 *
 * Behind the mosque's `store` capability and `store.manage`. Staff keep the catalogue — what is sold, for how much,
 * whether it is shown, whether it can be had now and (optionally) how many are left. With `store_checkout` on, members
 * buy in the app: Vipps reserves the money, and staff take it by marking the order «Utlevert» or «Sendt»
 * (ButikkOrdrer.tsx; the rules in lib/store-orders.ts). With it off, nobody can order or pay, and staff can still
 * prepare a DRAFT order to see what an order would hold. An order line keeps the title and price as ordered.
 *
 * Product text and picture descriptions are written in Norwegian and shown as written: they are never translated.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Package } from 'lucide-react';
import { errFields, errText, objectUrl, post, send, type Body } from '../lib/api';
import { useLoad, useMsg, useToastOn } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { osloTime } from '../lib/format';
import { Body as CardBody, Btn, Card, DropZone, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Store } from '../core';
import { krText, money, parseStock, parseWeight, sellerWarning, stockTag } from '../lib/store-orders';
import { DeliverySettingsForm, Orders, RatesForm, SellerForm, useCheckout } from './ButikkOrdrer';

/** As the public sees it: "Gratis", "149 kr", "149,50 kr". */
const shown = (ore: number) => (ore === 0 ? 'Gratis' : ore % 100 ? money(ore) : `${new Intl.NumberFormat('nb-NO').format(ore / 100)} kr`);
/** Each state as a word, a colour and what it means for the public — so the list can be read at a glance. */
const STATE: Record<string, { label: string; tone: TagTone; means: string }> = {
  published: { label: 'Publisert', tone: 'green', means: 'Vises i butikken.' },
  draft: { label: 'Utkast', tone: 'gold', means: 'Ikke vist for publikum ennå.' },
  archived: { label: 'Arkivert', tone: 'grey', means: 'Tatt ut av butikken. Vises ikke.' },
};

type ProductImage = { path: string; width: number; height: number; mime: string; bytes: number; decorative: boolean; alt: string | null };
type Product = { id: string; title: string; description: string | null; priceOre: number; state: string; availability: string; stock?: number | null; weightG?: number | null; updatedAt: string; image: ProductImage | null };
const EMPTY = { title: '', description: '', price: '', state: 'draft', availability: 'available', stock: '', weight: '' };
const LABELS = { title: 'Navn', priceOre: 'Pris', description: 'Beskrivelse', state: 'Status', availability: 'Tilgjengelighet', stock: 'Lager', weightG: 'Vekt', image: 'Bilde', alt: 'Bildebeskrivelse' };
/** The stored weight (grams) as the «Vekt» field shows it: empty when unknown. */
const weightInput = (g: number | null | undefined) => (g === null || g === undefined ? '' : String(g));
/** The stored stock as the «Lager» field shows it: empty when not counted. */
const stockText = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

/*
 * A product's picture. The same checks as the server (apps/api/src/product-image.js), made here first so a wrong
 * file is said at once, beside the box, instead of after an upload: JPG, PNG or WebP; at most 5 MB; the shorter
 * side at least 300 px, the longer at most 6000, and no more than 4:1. The server checks the bytes again, removes
 * all metadata (GPS, camera, author) and is the one that decides.
 */
/**
 * The server's own limits (api product-image.js). A picture larger than the server needs — over 2048 px on its long
 * side (what is stored), over its pixel limit, or over 5 MB — is first made smaller HERE, in the browser, as a plain
 * JPEG: what is uploaded is then cheap for the server to read (STORE.md, "Picture memory"). The server checks again
 * and decides; this only spares staff a refusal for an ordinary phone photo.
 */
const PIC = { types: ['image/jpeg', 'image/png', 'image/webp'], max: 5 * 1024 * 1024, original: 25 * 1024 * 1024,
  minSide: 300, maxPixels: 16_000_000, maxRatio: 4, send: 2048, quality: 0.9 };
type Pic = { data: string; w: number; h: number; bytes: number; scaled?: { w: number; h: number } };
const UNREADABLE_PIC = 'Bildet kunne ikke leses. Lagre det på nytt som JPG eller PNG.';

/** Whether a picture of this size is made smaller before it is sent. */
export const needsScaling = (w: number, h: number, bytes: number) => Math.max(w, h) > PIC.send || w * h > PIC.maxPixels || bytes > PIC.max;

const asDataUrl = (b: Blob) => new Promise<string>((resolve, reject) => {
  const r = new FileReader(); r.onerror = () => reject(r.error); r.onload = () => resolve(String(r.result || '')); r.readAsDataURL(b);
});

export async function readPicture(f: File): Promise<{ ok: true; pic: Pic } | { ok: false; message: string }> {
  if (!PIC.types.includes(f.type)) return { ok: false, message: 'Bildet må være JPG, PNG eller WebP.' };
  if (f.size > PIC.original) return { ok: false, message: `Bildet er for stort (${(f.size / 1048576).toFixed(1)} MB). Maks ${PIC.original / 1048576} MB.` };
  let bmp: ImageBitmap;
  // The phone's orientation applied, as the server does: the sides checked are the ones people see.
  try { bmp = await createImageBitmap(f, { imageOrientation: 'from-image' }); } catch { return { ok: false, message: UNREADABLE_PIC }; }
  const w = bmp.width, h = bmp.height;
  try {
    if (Math.min(w, h) < PIC.minSide) return { ok: false, message: `Bildet er for lite (${w}×${h}). Den korteste siden må være minst ${PIC.minSide} piksler.` };
    if (Math.max(w, h) / Math.min(w, h) > PIC.maxRatio) return { ok: false, message: `Bildet er for smalt eller for bredt (${w}×${h}). Den lengste siden kan være høyst ${PIC.maxRatio} ganger den korteste.` };
    if (!needsScaling(w, h, f.size)) return { ok: true, pic: { data: await asDataUrl(f), w, h, bytes: f.size } };
    const k = Math.min(1, PIC.send / Math.max(w, h));
    const sw = Math.max(1, Math.round(w * k)), sh = Math.max(1, Math.round(h * k));
    const canvas = document.createElement('canvas');
    canvas.width = sw; canvas.height = sh;
    const g = canvas.getContext('2d');
    if (!g) return { ok: false, message: UNREADABLE_PIC };
    g.fillStyle = '#ffffff'; g.fillRect(0, 0, sw, sh);      // transparency on white, as the server would
    g.imageSmoothingQuality = 'high';
    g.drawImage(bmp, 0, 0, sw, sh);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', PIC.quality));
    if (!blob || blob.size > PIC.max) return { ok: false, message: UNREADABLE_PIC };
    return { ok: true, pic: { data: await asDataUrl(blob), w: sw, h: sh, bytes: blob.size, scaled: { w, h } } };
  } catch { return { ok: false, message: UNREADABLE_PIC }; } finally { bmp.close(); }
}

/** The staff copy of a picture, fetched with the session: a draft's picture is not public. */
function useStaffPicture(path: string | null | undefined): { src: string | null; failed: boolean } {
  const [state, setState] = useState<{ src: string | null; failed: boolean }>({ src: null, failed: false });
  useEffect(() => {
    if (!path) { setState({ src: null, failed: false }); return; }
    let live = true, made: string | null = null;
    void objectUrl(path).then((u) => {
      if (!live) { if (u) URL.revokeObjectURL(u); return; }
      made = u; setState({ src: u, failed: !u });
    });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  return state;
}

/** A product's picture, or the placeholder: the same box either way, so the list does not jump. */
function Thumb({ product, src }: { product: Pick<Product, 'title' | 'image'>; src?: string | null }) {
  const staff = useStaffPicture(src === undefined && product.image ? product.image.path : null);
  const shownSrc = src !== undefined ? src : staff.src;
  return (
    <div className="shop-thumb">
      {shownSrc ? <img src={shownSrc} alt="" /> : (
        <span className="shop-thumb-none" aria-hidden="true"><Package size={28} strokeWidth={1.5} /></span>
      )}
      {!shownSrc && <span className="vh">{product.image && staff.failed ? 'Bildet kunne ikke vises' : 'Uten bilde'}</span>}
    </div>
  );
}

/** What the list says about a picture: none, decorative, described. */
function pictureTag(p: Product): [string, TagTone] {
  if (!p.image) return ['uten bilde', 'grey'];
  return p.image.decorative ? ['bilde: pynt', 'grey'] : ['bilde: beskrevet', 'green'];
}

type Desc = { decorative: boolean | null; alt: string };

/**
 * The picture's description: staff choose, explicitly, between a picture that SHOWS something (described, read
 * aloud by a screen reader) and one that is only decoration (skipped). Neither is preselected for a new picture.
 */
function DescribePicture({ desc, onChange, invalid }: { desc: Desc; onChange: (d: Desc) => void; invalid?: boolean }) {
  // A group has no valid aria-invalid (ARIA 1.2): what is wrong is said in its legend, which a screen reader reads on
  // entering the group, and the text field carries aria-invalid itself.
  const check = Store.imageDescription({ decorative: desc.decorative ?? undefined, alt: desc.alt });
  const problem = invalid && !check.ok ? check.errors.alt : '';
  return (
    <fieldset className={'shop-desc' + (problem ? ' invalid' : '')}>
      <legend>Bildebeskrivelse <i>*</i>{problem && <span className="shop-desc-err"> — {problem}</span>}</legend>
      <label className="shop-radio">
        <input type="radio" name="shop-desc" checked={desc.decorative === false} onChange={() => onChange({ ...desc, decorative: false })} />
        <span><b>Bildet viser noe</b> — beskriv det for dem som bruker skjermleser</span>
      </label>
      {desc.decorative === false && (
        <Field label="Hva viser bildet?" required className="field" note={`Én eller to setninger, på norsk. ${desc.alt.trim().length} av høyst ${Store.MAX_IMAGE_ALT} tegn. F.eks. «Koranen med grønt skinnomslag og gulltrykk».`}>
          {(id, d) => <input id={id} aria-describedby={d} aria-invalid={invalid || undefined} maxLength={Store.MAX_IMAGE_ALT} value={desc.alt}
                             onChange={(e) => onChange({ ...desc, alt: e.target.value })} />}
        </Field>
      )}
      <label className="shop-radio">
        <input type="radio" name="shop-desc" checked={desc.decorative === true} onChange={() => onChange({ decorative: true, alt: '' })} />
        <span><b>Bildet er bare pynt</b> — navnet og beskrivelsen sier alt; skjermlesere hopper over bildet</span>
      </label>
    </fieldset>
  );
}

/** `onSaved(text, tone)`: the outcome is said by the parent, which outlives this form (it remounts per product). */
function ProductForm({ editing, onSaved, onCancel, onDirty }: { editing: Product | null; onSaved: (text: string, tone: 'ok' | 'err') => void; onCancel: () => void; onDirty?: (d: boolean) => void }) {
  const [f, setF] = useState(() => (editing
    ? { title: editing.title, description: editing.description || '', price: krText(editing.priceOre), state: editing.state, availability: editing.availability || 'available', stock: stockText(editing.stock), weight: weightInput(editing.weightG) }
    : EMPTY));
  // The picture: a new one chosen (pic), or the current one marked for removal (drop). Saved with the product.
  const [pic, setPic] = useState<Pic | null>(null);
  const [drop, setDrop] = useState(false);
  const [picErr, setPicErr] = useState('');
  useToastOn(picErr, 'err');
  const startDesc: Desc = editing && editing.image ? { decorative: editing.image.decorative, alt: editing.image.alt || '' } : { decorative: null, alt: '' };
  const [desc, setDesc] = useState<Desc>(startDesc);
  const [bad, setBad] = useState<string[]>([]);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  // Archiving takes the product (and its picture) out of the shop: the button asks for a second click (destructive).
  const [armArchive, setArmArchive] = useState(false);
  const descChanged = desc.decorative !== startDesc.decorative || desc.alt.trim() !== startDesc.alt.trim();
  const dirty = Boolean(pic || drop || descChanged || (editing
    ? f.title !== editing.title || f.description !== (editing.description || '') || f.price !== krText(editing.priceOre) || f.state !== editing.state || f.availability !== (editing.availability || 'available') || f.stock.trim() !== stockText(editing.stock) || f.weight.trim() !== weightInput(editing.weightG)
    : f.title.trim() || f.description.trim() || f.price.trim() || f.stock.trim() || f.weight.trim()));
  useDirty(editing ? `butikk-${editing.id}` : 'butikk-new', dirty);
  useEffect(() => { onDirty && onDirty(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearBad = (k: string) => { if (bad.includes(k)) setBad(bad.filter((b) => b !== k)); };
  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setF({ ...f, [k]: e.target.value }); clearBad(k === 'price' ? 'priceOre' : k); if (k === 'state') setArmArchive(false);
  };
  const choose = async (file: File) => {
    setPicErr(''); say('');
    const r = await readPicture(file);
    if (!r.ok) { setPic(null); setPicErr(r.message); setBad([...bad.filter((b) => b !== 'image'), 'image']); return; }
    // A new picture needs its own description: the old one (or "decorative") may not fit it. Nothing is chosen for
    // staff — they describe it, or mark it decorative, again (STORE.md; review P2-2).
    setPic(r.pic); setDrop(false); clearBad('image'); setDesc({ decorative: null, alt: '' });
  };
  const current = editing && editing.image && !drop ? editing.image : null;
  const hasPicture = Boolean(pic || current);
  const archiving = f.state === 'archived' && (!editing || editing.state !== 'archived');

  const formRef = useRef<HTMLFormElement>(null);
  // After a refused save, focus goes to the first field that needs attention (review P3), not stays on the button.
  const focusFirstProblem = () => requestAnimationFrame(() => {
    const el = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], fieldset.shop-desc.invalid input');
    el?.focus();
  });
  // A save still running when staff open another product must not close that one (setEditing(null)) when it ends.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const said = (text: string, tone: 'ok' | 'err') => { if (alive.current) onSaved(text, tone); };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    say('');
    const price = Store.parseKroner(f.price);
    const errs: Record<string, string> = {};
    if (!f.title.trim()) errs.title = 'Oppgi et produktnavn.';
    if (!price.ok) errs.priceOre = price.message;
    const stock = parseStock(f.stock);
    if (!stock.ok) errs.stock = stock.message;
    const weight = parseWeight(f.weight);
    if (!weight.ok) errs.weightG = weight.message;
    const d = hasPicture ? Store.imageDescription({ decorative: desc.decorative ?? undefined, alt: desc.alt }) : null;
    if (d && !d.ok) errs.alt = d.errors.alt;
    if (Object.keys(errs).length) { setBad(Object.keys(errs)); focusFirstProblem(); return say(errText({ errors: errs }, LABELS), 'err'); }
    if (archiving && !armArchive) { setArmArchive(true); return say('Arkiverte varer og bildene deres vises ikke for publikum. Klikk «Arkiver varen» for å bekrefte.', ''); }
    setBusy(true);
    const body = { title: f.title, description: f.description, priceOre: price.ok ? price.ore : 0, state: f.state, availability: f.availability,
      stock: stock.ok ? stock.stock : null, weightG: weight.ok ? weight.weightG : null };
    const r = editing ? await send(`/store/products/${encodeURIComponent(editing.id)}`, { method: 'PUT', body: JSON.stringify(body) })
      : await post('/store/products', body);
    if (!r.ok) { setBusy(false); setBad(errFields(r.body)); setArmArchive(false); focusFirstProblem(); return say(errText(r.body, LABELS), 'err'); }
    const id: string = editing ? editing.id : r.body.id;
    // The product is saved. The picture is its own request: if it is refused, the product stays saved and the
    // message says exactly that — never "nothing was saved" when something was.
    const described = d && d.ok ? { decorative: d.value.decorative, alt: d.value.alt } : {};
    let picNote = '';
    if (pic) {
      const p = await send(`/store/products/${encodeURIComponent(id)}/image`, { method: 'PUT', body: JSON.stringify({ data: pic.data, ...described }) });
      if (!p.ok) picNote = `Varen er lagret, men bildet ble ikke lagret: ${errText(p.body)}`;
    } else if (drop && editing && editing.image) {
      const del = await send(`/store/products/${encodeURIComponent(id)}/image`, { method: 'DELETE' });
      if (!del.ok) picNote = `Varen er lagret, men bildet ble ikke fjernet: ${errText(del.body)}`;
    } else if (current && descChanged) {
      const p = await send(`/store/products/${encodeURIComponent(id)}/image`, { method: 'PATCH', body: JSON.stringify(described) });
      if (!p.ok) picNote = `Varen er lagret, men bildebeskrivelsen ble ikke lagret: ${errText(p.body)}`;
    }
    setBusy(false);
    if (picNote) { said(picNote, 'err'); return; }
    const name = `«${f.title.trim()}»`;
    const done = !editing ? `${name} er lagt til som ${STATE[f.state].label.toLowerCase()}.`
      : f.state !== editing.state ? `${name} er lagret og er nå ${STATE[f.state].label.toLowerCase()}. ${STATE[f.state].means}`
      : `${name} er lagret.`;
    said(done, 'ok');
  };
  const inv = (k: string) => bad.includes(k) || undefined;
  return (
    <form ref={formRef} onSubmit={save} aria-label={editing ? `Endre ${editing.title}` : 'Ny vare'} noValidate>
      <div className="row">
        <Field label="Navn" required className="field half">
          {(id) => <input id={id} required maxLength={Store.MAX_TITLE} aria-invalid={inv('title')} value={f.title} onChange={set('title')} />}
        </Field>
        <Field label="Pris (kr)" required className="field half" note="Hele kroner eller med øre, for eksempel 149 eller 149,50. 0 vises som «Gratis».">
          {(id, d) => <input id={id} required inputMode="decimal" autoComplete="off" aria-describedby={d} aria-invalid={inv('priceOre')} value={f.price} onChange={set('price')} />}
        </Field>
      </div>
      <div className="row">
        <Field label="Beskrivelse" className="field" style={{ flex: '1 1 100%' }} note={`Valgfri. ${f.description.length} av høyst ${Store.MAX_DESCRIPTION} tegn. Vises i sin helhet på varens side i appen.`}>
          {(id, d) => <textarea id={id} rows={3} maxLength={Store.MAX_DESCRIPTION} aria-describedby={d} aria-invalid={inv('description')} value={f.description} onChange={set('description')} />}
        </Field>
      </div>
      <div className="shop-pic-edit">
        <Thumb product={{ title: f.title, image: current }} src={pic ? pic.data : (current ? undefined : null)} />
        <div className="shop-pic-ctl">
          <DropZone title={pic ? 'Nytt bilde valgt — slipp et annet her for å bytte' : current ? 'Bytt bilde: slipp et nytt her, eller klikk' : 'Legg til bilde: slipp det her, eller klikk'}
                    accept={PIC.types.join(',')} onFile={(file) => { void choose(file); }}>
            <span className="note" style={{ display: 'block', marginTop: 4 }}>
              JPG, PNG eller WebP, høyst {PIC.original / 1048576} MB, minst {PIC.minSide} piksler på den korteste siden. Store bilder gjøres
              mindre før de sendes. Posisjon, kamera og annen skjult
              informasjon i bildet fjernes før det lagres. I varelisten vises bildet beskåret til 4:3; på varens egen side vises hele
              bildet. Det vises bare når varen er publisert.
              Ikke bruk bilder av personer.
            </span>
          </DropZone>
          {pic && <p className="field note" style={{ margin: '6px 0 0' }}>Nytt bilde: {pic.w}×{pic.h} piksler{pic.scaled ? ` (gjort mindre fra ${pic.scaled.w}×${pic.scaled.h})` : ''} · {(pic.bytes / 1048576).toFixed(1)} MB. Lagres når du lagrer varen.
            {' '}<Btn ghost onClick={() => { setPic(null); setPicErr(''); setDesc(startDesc); }}>Angre</Btn></p>}
          {!pic && current && <p className="field note" style={{ margin: '6px 0 0' }}>Nåværende bilde: {current.width}×{current.height} piksler.
            {' '}<TwoStep label="Fjern bildet" confirmLabel="Klikk igjen for å fjerne" onConfirm={() => {
              // The button goes away: focus goes to "Angre", and the live region below says what will happen.
              setDrop(true); requestAnimationFrame(() => document.getElementById('shop-undo-drop')?.focus());
            }} /></p>}
          {!pic && drop && <p className="field note" style={{ margin: '6px 0 0' }}>Bildet fjernes når du lagrer varen.
            {' '}<Btn ghost id="shop-undo-drop" onClick={() => setDrop(false)}>Angre</Btn></p>}
          <p className="vh" role="status">{!pic && drop ? 'Bildet fjernes når du lagrer varen.' : ''}</p>
          {hasPicture && <DescribePicture desc={desc} invalid={bad.includes('alt')} onChange={(d) => { setDesc(d); clearBad('alt'); }} />}
        </div>
      </div>
      <div className="row">
        <Field label="Status" className="field half" note={STATE[f.state] ? STATE[f.state].means + ' Bare publiserte varer vises i butikken.' : ''}>
          {(id, d) => (
            <select id={id} aria-describedby={d} aria-invalid={inv('state')} value={f.state} onChange={set('state')}>
              <option value="draft">Utkast (ikke vist)</option>
              <option value="published">Publisert</option>
              <option value="archived">Arkivert</option>
            </select>
          )}
        </Field>
        <Field label="Tilgjengelighet" className="field half" note="«Ikke tilgjengelig nå» viser varen, merket, og holder den utenfor ordrer.">
          {(id, d) => (
            <select id={id} aria-describedby={d} aria-invalid={inv('availability')} value={f.availability} onChange={set('availability')}>
              <option value="available">Tilgjengelig</option>
              <option value="unavailable">Ikke tilgjengelig nå</option>
            </select>
          )}
        </Field>
      </div>
      <div className="row">
        <Field label="Lager" className="field half" note="Tomt: telles ikke. Et tall: så mange kan selges; det telles ned når en kunde bestiller, og opp igjen når en ordre frigis. 0 vises som «Utsolgt».">
          {(id, d) => <input id={id} inputMode="numeric" autoComplete="off" aria-describedby={d} aria-invalid={inv('stock')} value={f.stock} onChange={set('stock')} />}
        </Field>
        <Field label="Vekt (gram)" className="field half" note="Brukes til frakt med transportør: kassen regner ut prisen fra vekten på varene i kurven. Tomt regnes som 500 g.">
          {(id, d) => <input id={id} inputMode="numeric" autoComplete="off" aria-describedby={d} aria-invalid={inv('weightG')} value={f.weight} onChange={set('weight')} />}
        </Field>
      </div>
      <Msg text={msg.text} tone={msg.tone} />
      <p className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn type="submit" disabled={busy} danger={archiving && armArchive}>
          {busy ? 'Lagrer …' : archiving ? (armArchive ? 'Arkiver varen' : 'Lagre og arkiver') : editing ? 'Lagre endringene' : 'Legg til vare'}
        </Btn>
        <Btn type="button" ghost onClick={onCancel}>Avbryt</Btn>
      </p>
    </form>
  );
}

// Owner 2026-10-23: in a popup from «+ Nytt ordreutkast»; on success the page says the result (`onMade(text)`).
function DraftOrder({ products, onMade }: { products: Product[]; onMade: (text: string) => void }) {
  // Only what an order may hold: published and available now (core store.priceOrder refuses the rest).
  const published = products.filter((p) => p.state === 'published' && (p.availability || 'available') === 'available');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  // Only lines for products that can still be ordered: after a reload, a product unpublished or marked not available
  // meanwhile drops out of the order instead of being sent and refused (review P3).
  const items = Object.entries(qty).filter(([id, q]) => q > 0 && published.some((p) => p.id === id)).map(([productId, quantity]) => ({ productId, quantity }));
  const total = items.reduce((s, l) => s + ((published.find((p) => p.id === l.productId) || { priceOre: 0 }).priceOre * l.quantity), 0);
  const make = async () => {
    setBusy(true);
    const r = await post('/store/orders', { items });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setQty({});
    onMade(`Ordreutkast laget: ${money(r.body.totalOre)}. Ingen betaling er startet.`);
  };
  if (!published.length) return <p className="field note" style={{ margin: '6px 18px' }}>Publiser minst én tilgjengelig vare for å lage et ordreutkast.</p>;
  return (
    <div>
      <table className="t">
        <thead><tr><th scope="col">Vare</th><th scope="col">Pris</th><th scope="col">Antall</th></tr></thead>
        <tbody>{published.map((p) => (
          <tr key={p.id}>
            <td>{p.title}</td>
            <td>{shown(p.priceOre)}</td>
            <td>
              <input type="number" min={0} max={Store.MAX_QUANTITY} step={1} aria-label={`Antall ${p.title}`} style={{ width: 80 }}
                     value={qty[p.id] || 0}
                     onChange={(e) => { const n = Math.max(0, Math.min(Store.MAX_QUANTITY, Math.trunc(Number(e.target.value) || 0))); setQty({ ...qty, [p.id]: n }); }} />
            </td>
          </tr>))}
        </tbody>
      </table>
      <p className="field note" style={{ margin: '6px 18px' }}>Sum: <strong>{money(total)}</strong> (beregnes på nytt av serveren med prisene nå).</p>
      <Msg text={msg.text} tone={msg.tone} />
      <p style={{ margin: '6px 18px' }}><Btn disabled={busy || !items.length} onClick={make}>{busy ? 'Lager …' : 'Lag ordreutkast'}</Btn></p>
    </div>
  );
}

const FILTERS: [string, string][] = [['all', 'Alle'], ['published', 'Publisert'], ['draft', 'Utkast'], ['archived', 'Arkivert']];

const TABS: [string, string][] = [['varer', 'Varer'], ['ordrer', 'Ordrer'], ['levering', 'Levering']];

export function Butikk() {
  const products = useLoad<Body>('/store/products');
  const { checkout, set: setCheckout } = useCheckout();
  const checkoutOn = checkout.state === 'on';
  // Audit F-04: payment is switched on, but members cannot order until the seller's details are written.
  const sellerBlock = checkout.state === 'on' ? sellerWarning(checkout.settings) : null;
  const [editing, setEditing] = useState<Product | null>(null);
  // The outcome of the last save, above the form: the form itself remounts and would lose it (STORE_REVIEW F5).
  const [saved, saidSaved] = useMsg();
  const [filter, setFilter] = useState('all');
  // Orders first once the shop takes payment (that is where the work is); the catalogue first while it does not.
  const [tab, setTab] = useState<string | null>(null);
  const [ordersKey, setOrdersKey] = useState(0);
  // Owner 2026-10-23: «+ Ny vare» and «Endre» open the product form in a popup; so does «+ Nytt ordreutkast».
  const [creating, setCreating] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftMsg, sayDraft] = useMsg();
  if (products.error) return <LoadError error={products.error} retry={products.reload} />;
  if (!products.data || checkout.state === 'loading') return <Loading />;
  const shownTab = tab && (tab !== 'levering' || checkoutOn) ? tab : sellerBlock ? 'levering' : checkoutOn ? 'ordrer' : 'varer';
  const tabs = TABS.filter(([k]) => k !== 'levering' || checkoutOn);
  const list: Product[] = products.data.items || [];
  const count = (k: string) => (k === 'all' ? list.length : list.filter((p) => p.state === k).length);
  // Published first, then drafts, then the archive: what the public sees is what staff check first.
  const ORDER = ['published', 'draft', 'archived'];
  const shownList = list.filter((p) => filter === 'all' || p.state === filter)
    .sort((a, b) => ORDER.indexOf(a.state) - ORDER.indexOf(b.state) || a.title.localeCompare(b.title, 'nb'));
  const edit = (p: Product) => { saidSaved(''); setFormDirty(false); setEditing(p); };
  const closeForm = () => { setEditing(null); setCreating(false); setFormDirty(false); };
  return (
    <>
      <Card title="Butikk">
        {sellerBlock
          ? <p className="field note" style={{ margin: '6px 18px' }}><Tag tone="gold">Stengt</Tag>{' '}Betaling er slått på, men ingen kan bestille før selgeropplysningene er fylt inn (se under).</p>
          : !checkoutOn && <p className="field note" style={{ margin: '6px 18px' }}><Tag tone="gold">Ikke åpnet</Tag>{' '}Ingen kan bestille eller betale ennå.</p>}
        {sellerBlock && <p className="msg warn" role="alert" style={{ margin: '6px 18px' }}>{sellerBlock}</p>}
        {checkout.state === 'error' && <p className="msg err" role="alert" style={{ margin: '6px 18px' }}>Kunne ikke hente leveringsinnstillingene: {checkout.message}</p>}
        <div className="bnr-seg shop-filter" role="group" aria-label="Butikk: velg del">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={shownTab === k} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
      </Card>
      {shownTab === 'ordrer' && <Orders key={ordersKey} checkoutOn={checkoutOn} />}
      {shownTab === 'levering' && checkout.state === 'on' && (
        <Card title="Levering" hint="Hvordan kundene får varene. Kunden velger ved kassen blant det som er slått på.">
          <CardBody><DeliverySettingsForm settings={checkout.settings} onSaved={setCheckout} /></CardBody>
        </Card>
      )}
      {shownTab === 'levering' && checkout.state === 'on' && checkout.settings.shipping && checkout.settings.shippingMode === 'carrier' && (
        <Card title="Fraktsatser" hint="Prisen kunden betaler for frakt, per transportør og vekt: «PostNord inntil 2 kg: 99 kr». Kassen velger den minste vekten som rommer kurven. Varer uten vekt regnes som 500 g.">
          <CardBody><RatesForm key={(checkout.settings.shippingCarriers || []).join()} settings={checkout.settings} onSaved={setCheckout} /></CardBody>
        </Card>
      )}
      {shownTab === 'levering' && checkout.state === 'on' && (
        <Card title="Selger" hint="Vises for kunden i salgsvilkårene før de betaler: hvem som selger, og hvordan selgeren kan nås for spørsmål, angrerett og klager. Felt med * må fylles inn før medlemmene kan bestille.">
          <CardBody><SellerForm settings={checkout.settings} onSaved={setCheckout} /></CardBody>
        </Card>
      )}
      {shownTab === 'varer' && <>
      <div className="new-row">
        <Btn small={false} onClick={() => { saidSaved(''); setFormDirty(false); setEditing(null); setCreating(true); }}>+ Ny vare</Btn>
        {!checkoutOn && <Btn ghost small={false} onClick={() => { sayDraft(''); setDrafting(true); }}>+ Nytt ordreutkast</Btn>}
      </div>
      {/* The outcome, and where focus goes after a save: the popup has closed, so focus would otherwise fall to the page. */}
      <div id="butikk-saved" tabIndex={-1}><Msg text={saved.text} tone={saved.tone} /></div>
      {!drafting && <Msg text={draftMsg.text} tone={draftMsg.tone} />}
      <Card title="Varer">
        {list.length > 0 && (
          <div className="bnr-seg shop-filter" role="group" aria-label="Vis varer">
            {FILTERS.map(([k, label]) => (
              <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{label} <span className="shop-count">{count(k)}</span></button>
            ))}
          </div>
        )}
        {/* Cards rather than a table: a picture, a name and a price read at a glance, and they wrap on a phone
            instead of scrolling sideways. */}
        <ul className="shop-grid" aria-label="Varer">{shownList.map((p) => {
          const st = STATE[p.state] || { label: p.state, tone: 'grey' as TagTone, means: '' };
          const [picLabel, picTone] = pictureTag(p);
          const left = stockTag(p.stock);
          return (
            <li key={p.id} className={'shop-card st-' + p.state + (editing && editing.id === p.id ? ' sel' : '')}>
              <Thumb product={p} />
              <div className="shop-card-body">
                <b className="shop-card-title">{p.title}</b>
                <span className="shop-card-price">{shown(p.priceOre)}</span>
                <span className="shop-tags">
                  <Tag tone={st.tone}>{st.label}</Tag>
                  {p.availability === 'unavailable' && <Tag tone="red">ikke tilgjengelig nå</Tag>}
                  {left && <Tag tone={left[1]}>{left[0]}</Tag>}
                  <Tag tone={picTone}>{picLabel}</Tag>
                </span>
                <span className="field note" style={{ margin: 0 }}>Endret {osloTime(p.updatedAt)}</span>
              </div>
              <Btn ghost onClick={() => edit(p)} aria-label={`Endre ${p.title}`}>Endre</Btn>
            </li>);
        })}</ul>
        {!list.length && (
          <div className="shop-empty">
            <Package size={30} strokeWidth={1.4} aria-hidden="true" />
            <p><b>Ingen varer ennå.</b> Legg til den første med «+ Ny vare». Den lagres som utkast, og vises ikke for publikum før du publiserer den.</p>
          </div>
        )}
        {list.length > 0 && !shownList.length && <p className="field note" style={{ margin: '10px 18px' }}>Ingen varer med denne statusen.</p>}
      </Card>
      <FormDialog open={creating || Boolean(editing)} wide title={editing ? `Endre «${editing.title}»` : 'Ny vare'} dirty={formDirty} onClose={closeForm}>
          <ProductForm key={editing ? editing.id : 'new'} editing={editing} onCancel={closeForm} onDirty={setFormDirty}
                       onSaved={(text, tone) => { closeForm(); saidSaved(text, tone); products.reload();
                         requestAnimationFrame(() => document.getElementById('butikk-saved')?.focus()); }} />
      </FormDialog>
      {!checkoutOn && (
        <FormDialog open={drafting} title="Nytt ordreutkast" onClose={() => setDrafting(false)}
          hint="For å se hva en ordre vil inneholde. Ingen kunde, ingen betaling. Utkastene står under «Ordrer».">
          <DraftOrder products={list} onMade={(text) => { setDrafting(false); sayDraft(text, 'ok'); setOrdersKey((k) => k + 1); }} />
        </FormDialog>
      )}
      </>}
    </>
  );
}
